import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  PhoneIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { api, showNotification, NOTIFICATION_TYPES } from '../utils/apiClient';
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants';

const SuccessAnimation = ({ message }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
    <div className="bg-[#1C1C1C] rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/10 animate-scale-bounce">
      <div className="text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-full animate-pulse-slow" />
          <div className="absolute inset-2 bg-[#1C1C1C] rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                className="animate-check-draw"
                strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}
                d="M5 13l4 4L19 7"
                strokeDasharray="30" strokeDashoffset="30"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2 animate-slide-up">Success!</h3>
        <p className="text-gray-400 animate-slide-up-delay">{message}</p>
      </div>
    </div>
  </div>
);

const RedirectingAnimation = ({ message = 'Redirecting to Registration' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
    <div className="bg-[#1C1C1C] rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/10 animate-scale-bounce">
      <div className="text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <div className="absolute inset-0">
            <div className="w-full h-full border-4 border-[#FF6B35]/20 rounded-full" />
            <div className="absolute inset-0 w-full h-full border-4 border-[#FF6B35] rounded-full border-t-transparent animate-spin-slow" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-full animate-pulse" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">{message}</h3>
        <p className="text-gray-400">Please wait a moment...</p>
        <div className="flex justify-center space-x-2 mt-4">
          {[0, 150, 300].map((delay) => (
            <div key={delay} className="w-2 h-2 bg-[#FF6B35] rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const OTPInput = ({ length = 4, onComplete, loading = false }) => {
  const inputRef = useRef([]);
  const [otp, setOTP] = useState(Array(length).fill(''));

  useEffect(() => { inputRef.current[0]?.focus(); }, []);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    if (value && index < length - 1) inputRef.current[index + 1]?.focus();
    if (!value && index > 0) inputRef.current[index - 1]?.focus();
    if (newOTP.every(d => d !== '') && newOTP.join('').length === length) onComplete(newOTP.join(''));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRef.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').slice(0, length);
    if (!/^\d+$/.test(pasted)) return;
    const newOTP = Array(length).fill('');
    pasted.split('').forEach((d, i) => { if (i < length) newOTP[i] = d; });
    setOTP(newOTP);
    inputRef.current[Math.min(pasted.length - 1, length - 1)]?.focus();
    if (newOTP.join('').length === length) onComplete(newOTP.join(''));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-3">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={otp[index]}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            onChange={e => handleChange(e.target.value, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={ref => (inputRef.current[index] = ref)}
            disabled={loading}
            className={`w-14 h-14 text-center text-xl font-bold border-2 rounded-xl bg-[#1C1C1C] text-white
              ${otp[index] ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-white/10'}
              focus:border-[#FF6B35] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20
              disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            placeholder="•"
          />
        ))}
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-500 flex-shrink-0">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.529 5.845L.057 23.428a.5.5 0 00.609.61l5.703-1.494A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.74-.524-5.29-1.432l-.38-.222-3.384.887.902-3.296-.246-.395A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
          </svg>
          <p className="text-sm text-green-400">Check your <strong>WhatsApp</strong> for the OTP</p>
        </div>
      </div>
    </div>
  );
};

const Login = () => {
  const [step, setStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [developmentMode, setDevelopmentMode] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [showOtpSuccess, setShowOtpSuccess] = useState(false);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      if (location.state.phoneNumber) setPhoneNumber(location.state.phoneNumber);
    }
  }, [location]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const validateForm = () => {
    const phone = phoneNumber.trim();
    if (!phone) { setError('Phone number is required'); return false; }
    if (!/^\d{10}$/.test(phone)) { setError('Please enter a valid 10-digit phone number'); return false; }
    if (!['6', '7', '8', '9'].includes(phone[0])) { setError('Phone number should start with 6, 7, 8, or 9'); return false; }
    return true;
  };

  const isUserNotRegistered = (errorMessage, status) => {
    if (!errorMessage) return false;
    const msg = errorMessage.toLowerCase();
    return status === 404 || msg.includes('user not found') || msg.includes('not found with phone') || msg.includes('not registered') || msg.includes('does not exist');
  };

  const redirectToRegister = (message = 'Phone number not registered') => {
    setRedirecting(true);
    setError('');
    showNotification(`${message}. Redirecting to registration...`, NOTIFICATION_TYPES.INFO);
    const urlParams = new URLSearchParams(location.search);
    const returnUrl = urlParams.get('returnUrl');
    setTimeout(() => {
      navigate(`/register?returnUrl=${returnUrl || '/'}`, {
        state: { phoneNumber: phoneNumber.trim(), message: 'Please register to continue' },
        replace: true
      });
    }, 2500);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.post(API_ENDPOINTS.AUTH.SEND_LOGIN_OTP, { phone: phoneNumber.trim() });
      if (result && (result.status === 'true' || result.status === true || result.success !== false)) {
        const responseData = result.data || result;
        setDevelopmentMode(responseData.developmentMode === 'true' || responseData.developmentMode === true || false);
        setShowOtpSuccess(true);
        showNotification('OTP sent successfully', NOTIFICATION_TYPES.SUCCESS);
        setTimeout(() => {
          setShowOtpSuccess(false);
          setSuccess(responseData.message || 'OTP sent successfully');
          setStep('otp');
          setCountdown(30);
        }, 2000);
      } else {
        throw new Error(result?.message || 'User not registered. Please register first.');
      }
    } catch (err) {
      const status = err.response?.status;
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message;
      if (isUserNotRegistered(errorMessage, status)) { redirectToRegister('Phone number not registered. Please register first'); return; }
      let displayMessage = 'Failed to send OTP. Please try again.';
      if (status === 400) displayMessage = 'Invalid phone number. Please check and try again.';
      else if (status === 429) displayMessage = 'Too many requests. Please wait a moment and try again.';
      else if (status === 500) displayMessage = 'Server error. Please try again in a few moments.';
      else if (status === 503) displayMessage = 'Service temporarily unavailable. Please try again later.';
      else if (errorMessage) displayMessage = errorMessage;
      setError(displayMessage);
      showNotification(displayMessage, NOTIFICATION_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const result = await api.post(API_ENDPOINTS.AUTH.VERIFY_LOGIN_OTP, { phone: phoneNumber.trim(), otp: otpCode.trim() });
      const token = result.data?.token || result.token;
      const user = result.data?.user || result.user;
      const message = result.data?.message || result.message || 'Login successful';
   if (!token) {
  throw new Error(
    "Authentication token not received"
  );
}

// ==========================================
// SAVE JWT FOR WEBSITE
// ==========================================
localStorage.setItem(
  STORAGE_KEYS.TOKEN,
  token
);


// ==========================================
// SEND JWT TO ANDROID WEBVIEW
// ==========================================
if (
  typeof window !== "undefined" &&
  window.ReactNativeWebView &&
  typeof window.ReactNativeWebView.postMessage ===
    "function"
) {
  console.log(
    "📱 SENDING AUTH TOKEN TO ANDROID"
  );

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      type: "AUTH_TOKEN",
      token: token,
    })
  );
} else {
  console.log(
    "🌐 NORMAL BROWSER - NOT ANDROID WEBVIEW"
  );
}


// ==========================================
// SAVE USER
// ==========================================
if (user) {
  localStorage.setItem(
    STORAGE_KEYS.USER,
    JSON.stringify(user)
  );
} else {
        try {
          const profileResult = await api.get(API_ENDPOINTS.AUTH.PROFILE);
          if (profileResult?.success && profileResult.data) localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profileResult.data));
          else throw new Error('Profile load failed');
        } catch {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({ phoneNumber: phoneNumber.trim(), loginTime: new Date().toISOString() }));
        }
      }
      setShowLoginSuccess(true);
      showNotification(message, NOTIFICATION_TYPES.SUCCESS);
      const urlParams = new URLSearchParams(location.search);
      const returnUrl = urlParams.get('returnUrl');
      setTimeout(() => { window.location.href = returnUrl ? decodeURIComponent(returnUrl) : '/'; }, 2000);
    } catch (err) {
      console.error('❌ LOGIN - Verification Error:', err);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpLoading(true);
    setOtpError('');
    setSuccess('');
    try {
      const result = await api.post(API_ENDPOINTS.AUTH.SEND_LOGIN_OTP, { phone: phoneNumber.trim() });
      if (result && (result.status === 'true' || result.status === true || result.success !== false)) {
        setSuccess('OTP resent successfully');
        setCountdown(30);
        showNotification('New OTP sent to your phone', NOTIFICATION_TYPES.SUCCESS);
      } else {
        throw new Error('Failed to resend OTP');
      }
    } catch (err) {
      const status = err.response?.status;
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message;
      if (isUserNotRegistered(errorMessage, status)) { redirectToRegister('Account not found. Please register first'); return; }
      let displayMessage = 'Failed to resend OTP. Please try again.';
      if (errorMessage?.includes('Please wait')) displayMessage = errorMessage;
      else if (status === 429) displayMessage = 'Too many requests. Please wait before retrying.';
      else if (status === 500) displayMessage = 'Server error. Please try again.';
      else if (errorMessage) displayMessage = errorMessage;
      setOtpError(displayMessage);
      showNotification(displayMessage, NOTIFICATION_TYPES.ERROR);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
      {showOtpSuccess && <SuccessAnimation message="OTP sent to your WhatsApp!" />}
      {showLoginSuccess && <SuccessAnimation message="Login successful! Redirecting..." />}
      {redirecting && <RedirectingAnimation />}

      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center py-12 px-4 sm:px-6">

        {/* Background grid */}
        <div
          className="fixed inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative w-full max-w-md">

          {/* Brand */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6 group">
              <img src="/logo.png" alt="OkMechanic" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-white font-bold text-xl group-hover:text-[#FF6B35] transition-colors">
                Ok<span className="text-[#FF6B35]">Mechanic</span>
              </span>
            </Link>
          </div>

          {step === 'phone' ? (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF6B35]/10 rounded-2xl mb-4 border border-[#FF6B35]/20">
                  <PhoneIcon className="h-7 w-7 text-[#FF6B35]" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Welcome Back</h2>
                <p className="text-gray-500 text-sm sm:text-base">Sign in with your phone number via OTP</p>
              </div>

              <div className="bg-[#1A1A1A] rounded-2xl p-6 sm:p-8 border border-white/[0.06]">
                <form onSubmit={handleSendOTP} className="space-y-5">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-start animate-shake">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}

                  {success && (
                    <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-xl flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                      <span className="text-sm">{success}</span>
                    </div>
                  )}

                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="absolute inset-y-0 left-11 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm font-medium">+91</span>
                      </div>
                      <input
                        id="phoneNumber"
                        type="tel"
                        value={phoneNumber}
                        onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        maxLength={10}
                        required
                        className="block w-full pl-[4.5rem] pr-4 py-3.5 bg-[#111111] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition-all text-base"
                        placeholder="9876543210"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || phoneNumber.length !== 10}
                    className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl text-base font-bold text-white bg-[#FF6B35] hover:bg-[#E85520] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#FF6B35]/20 hover:shadow-[#FF6B35]/30 hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        <PhoneIcon className="h-5 w-5 mr-2" />
                        Send Login OTP
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center mt-5 pt-5 border-t border-white/10">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-semibold text-[#FF6B35] hover:text-[#E85520] transition-colors">
                      Register here
                    </Link>
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF6B35]/10 rounded-2xl mb-4 border border-[#FF6B35]/20">
                  <ShieldCheckIcon className="h-7 w-7 text-[#FF6B35]" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Verify Your Phone</h2>
                <p className="text-gray-500 mb-1 text-sm sm:text-base">Enter the 4-digit code sent to</p>
                <p className="text-[#FF6B35] font-bold text-lg">+91 {phoneNumber}</p>
                <p className="text-sm text-green-400 mt-2 flex items-center justify-center gap-1.5">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-green-500 inline flex-shrink-0">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.529 5.845L.057 23.428a.5.5 0 00.609.61l5.703-1.494A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.74-.524-5.29-1.432l-.38-.222-3.384.887.902-3.296-.246-.395A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                  </svg>
                  OTP sent to your WhatsApp
                </p>
              </div>

              <div className="bg-[#1A1A1A] rounded-2xl p-6 sm:p-8 border border-white/[0.06]">
                <div className="space-y-5">
                  {otpError && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-start animate-shake">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{otpError}</span>
                    </div>
                  )}
                  {success && (
                    <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-xl flex items-center">
                      <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                      <span className="text-sm">{success}</span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-4 text-center">
                      Enter Verification Code
                    </label>
                    <OTPInput length={4} onComplete={handleVerifyOTP} loading={otpLoading} />
                  </div>

                  <div className="flex justify-center">
                    {countdown > 0 ? (
                      <div className="flex items-center text-gray-500 text-sm">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        <span>Resend OTP in {countdown}s</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={otpLoading}
                        className="text-[#FF6B35] hover:text-[#E85520] font-semibold disabled:opacity-50 transition-colors text-sm"
                      >
                        {otpLoading ? 'Sending...' : 'Resend OTP'}
                      </button>
                    )}
                  </div>

                  <div className="text-center pt-4 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => { setStep('phone'); setError(''); setOtpError(''); setSuccess(''); }}
                      className="flex items-center justify-center text-gray-500 hover:text-gray-300 font-medium mx-auto transition-colors text-sm"
                    >
                      <ArrowLeftIcon className="h-4 w-4 mr-1" />
                      Change Phone Number
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scale-bounce {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes check-draw { from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; } }
        @keyframes pulse-slow { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.05); } }
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes slide-up-delay { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-8px); } 75% { transform: translateX(8px); } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-scale-bounce { animation: scale-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }
        .animate-check-draw { animation: check-draw 0.6s ease-out 0.3s forwards; }
        .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
        .animate-slide-up { animation: slide-up 0.4s ease-out 0.5s both; }
        .animate-slide-up-delay { animation: slide-up-delay 0.4s ease-out 0.6s both; }
        .animate-spin-slow { animation: spin-slow 1.5s linear infinite; }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </>
  );
};

export default Login;
